import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../../utils/responsiveMedia';
import PromotionBanner from '../../PromotionBanner';
import SalonTemplateAbout from '../blocks/SalonTemplateAbout';
import SalonTemplateBookingAction from '../blocks/SalonTemplateBookingAction';
import SalonTemplateContactLocation from '../blocks/SalonTemplateContactLocation';
import SalonTemplateGallery from '../blocks/SalonTemplateGallery';
import SalonTemplateIdentity from '../blocks/SalonTemplateIdentity';
import SalonTemplateOpeningHours from '../blocks/SalonTemplateOpeningHours';
import SalonTemplateReviews from '../blocks/SalonTemplateReviews';
import SalonTemplateServices from '../blocks/SalonTemplateServices';
import SalonTemplateTeam from '../blocks/SalonTemplateTeam';


const SectionTitle = ({ children }) => {
  const { tokens: theme } = useSalonPublicTheme();
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        component="h2"
        data-salon-text-role="title"
        variant="h5"
        sx={{
          color: theme.colors.gray[700],
          fontWeight: 600,
          mb: 1,
          fontSize: '1.5rem',
          letterSpacing: '0.025em',
          textAlign: 'left',
        }}
      >
        {children}
      </Typography>
      <Box sx={{ width: 60, height: 3, bgcolor: theme.colors.secondary[400], borderRadius: 2 }} />
    </Box>
  );
};

const SalooneSignatureTemplate = ({
  viewModel,
  actions,
  mode = 'public',
  viewport = 'desktop',
  direction = 'ltr',
}) => {
  const { t } = useTranslation();
  const { layout, tokens: theme } = useSalonPublicTheme();
  const identity = viewModel?.identity || {};
  const photos = Array.isArray(viewModel?.photos) ? viewModel.photos : [];
  const reviews = Array.isArray(viewModel?.reviews) ? viewModel.reviews : [];
  const publicState = viewModel?.publicState || {};
  const visibility = layout.tokens.visibility;
  const contentLayout = layout.tokens.content;
  const isMobile = viewport === 'mobile';
  const sidebarIsStacked = contentLayout.sidebar === 'stacked';
  const sidebarIsStart = contentLayout.sidebar === 'start';
  const hasBookableService = Boolean(
    viewModel?.capabilities?.canClientBook
    && viewModel?.serviceCategories?.some((category) => (
      category?.services?.some((service) => service?.canBook && service?.raw)
    )),
  );
  const hasDesktopSidebarContent = Boolean(
    visibility.opening_hours
    || visibility.reviews
    || identity.description
  );
  const reviewData = {
    items: reviews,
    averageRating: viewModel?.averageRating,
    isAuthenticated: Boolean(publicState.isAuthenticated),
    isLoading: Boolean(publicState.reviewsLoading),
    error: publicState.reviewsError || null,
  };

  return (
    <Box
      data-testid="salon-template-renderer"
      data-salon-template="saloone_signature"
      data-mode={mode}
      data-viewport={viewport}
      dir={direction}
      sx={{ display: 'contents' }}
    >
      <SalonTemplateIdentity
        data={{
          ...identity,
          heroPhoto: photos[0]?.src,
          averageRating: viewModel?.averageRating,
          reviewCount: reviews.length,
          isFavorite: viewModel?.capabilities?.isFavorite,
        }}
        variant="immersive"
        mode={mode}
        onBack={actions?.onBack}
        onToggleFavorite={actions?.onToggleFavorite}
        onOpenMobileInfo={actions?.onOpenMobileInfo}
        onOpenHeaderInfo={actions?.onOpenHeaderInfo}
        onBook={actions?.onOpenBooking}
        onContact={actions?.onContact}
        clientChatEnabled={identity.raw?.client_chat_enabled}
        bookingAction={hasBookableService ? (
          <SalonTemplateBookingAction
            data={{ available: true, targetId: 'services' }}
            variant="hero"
            mode={mode}
            onBook={actions?.onOpenBooking}
          />
        ) : null}
      />

      <Box
        sx={{
          flex: 1,
          backgroundColor: theme.salon.background,
          width: '100%',
          overflowY: isMobile ? 'visible' : 'auto',
          overflowX: 'hidden',
          [HIGH_DENSITY_TABLET_MEDIA]: { overflowY: 'visible' },
        }}
      >
        <Box
          sx={{
            maxWidth: 1400,
            mx: 'auto',
            py: { xs: 3, md: 6 },
            px: { xs: 2, md: 4, lg: 4 },
            boxSizing: 'border-box',
            [HIGH_DENSITY_TABLET_MEDIA]: { maxWidth: 1760, py: 7, px: 6 },
          }}
        >
          <PromotionBanner
            message={publicState.promoBanner?.message}
            style={publicState.promoBanner?.style}
            size="lg"
            sx={{ mb: 3 }}
          />
          <SalonTemplateOpeningHours
            data={{ hours: [], closures: viewModel?.closures }}
            variant="closures"
          />

          <Grid
            container
            data-testid="salon-content-layout"
            data-sidebar={contentLayout.sidebar}
            data-alignment={contentLayout.alignment}
            spacing={{ xs: 3, md: 8, lg: 8 }}
            sx={{ mx: 0, width: '100%' }}
          >
            <Grid
              component="main"
              item
              xs={12}
              md={sidebarIsStacked || !hasDesktopSidebarContent ? 12 : 8}
              lg={sidebarIsStacked || !hasDesktopSidebarContent ? 12 : 9}
              data-sidebar-empty={hasDesktopSidebarContent ? 'false' : 'true'}
              sx={{ pl: 0, order: sidebarIsStart ? 2 : 1 }}
            >
              <SalonTemplateGallery
                data={photos}
                salonId={identity.id}
                variant="saloone"
                visible={visibility.gallery}
                mode={mode}
                onSelectPhoto={actions?.onSelectPhoto}
                onNavigatePhoto={actions?.onNavigatePhoto}
                selectedIndex={publicState.photoIndex}
                title={<SectionTitle>{t('landing_salon_details.photo_gallery')}</SectionTitle>}
              />

              <SalonTemplateServices
                data={viewModel?.serviceCategories}
                variant="saloone"
                mode={mode}
                loading={Boolean(publicState.servicesLoading)}
                onBookService={actions?.onBookService}
              />

              <SalonTemplateTeam
                data={viewModel?.team}
                variant="cards"
                visible={visibility.team}
                mode={mode}
                onSelectMember={actions?.onSelectTeamMember}
              />

              {isMobile ? (
                <SalonTemplateReviews
                  data={reviewData}
                  variant="cards"
                  visible={visibility.reviews}
                  mode={mode}
                  onOpenReviewComposer={actions?.onOpenReviewComposer}
                  onOpenAllReviews={actions?.onOpenAllReviews}
                />
              ) : null}

              <SalonTemplateContactLocation
                data={identity}
                variant="map"
                mode={mode}
                visible={Boolean(
                  visibility.location
                  && identity.latitude != null
                  && identity.longitude != null
                )}
              />
            </Grid>

            {!isMobile && hasDesktopSidebarContent ? (
              <Grid
                component="aside"
                item
                xs={12}
                md={sidebarIsStacked ? 12 : 4}
                lg={sidebarIsStacked ? 12 : 3}
                sx={{
                  display: { xs: 'none', md: 'block' },
                  pl: 0,
                  pr: 3,
                  order: sidebarIsStart ? 1 : 2,
                }}
              >
                <Box>
                  <SalonTemplateOpeningHours
                    data={{ hours: viewModel?.openingHours, closures: [] }}
                    variant="card"
                    visible={visibility.opening_hours}
                    includeClosures={false}
                  />
                  <SalonTemplateReviews
                    data={reviewData}
                    variant="cards"
                    visible={visibility.reviews}
                    mode={mode}
                    onOpenReviewComposer={actions?.onOpenReviewComposer}
                    onOpenAllReviews={actions?.onOpenAllReviews}
                  />
                  <SalonTemplateAbout data={identity} visible={Boolean(identity.description)} />
                </Box>
              </Grid>
            ) : null}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default SalooneSignatureTemplate;
